import React, { useEffect, useState } from "react";
import { ShoppingBag, ShoppingCart, Trash2, Crown } from "lucide-react";
import { toast } from "sonner";
import { TurfTalk, newIdempotencyKey } from "@/api/client";
import { PageHeader, Empty, Skeleton } from "@/components/ui-kit";
import { Modal, TextField } from "@/components/Modal";

const money = (c) => `$${(c / 100).toFixed(2)}`;

function CheckoutModal({ open, onClose, onDone }) {
  const [f, setF] = useState({ shipping_name: "", shipping_address: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (f.shipping_name.length < 2 || f.shipping_address.length < 4) { toast.error("Enter a name and address."); return; }
    setBusy(true);
    try { const r = await TurfTalk.checkout({ idempotency_key: newIdempotencyKey(), ...f }); toast.success("Order placed (sandbox — no real charge)"); onDone?.(r.order); onClose(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Checkout (sandbox)" testid="checkout-modal">
      <div className="mb-3 text-[12px] text-[#F4B740]">Sandbox checkout — billing is deferred, no real charge is made.</div>
      <TextField label="Shipping name" value={f.shipping_name} onChange={(e) => setF((s) => ({ ...s, shipping_name: e.target.value }))} data-testid="checkout-name" />
      <TextField label="Shipping address" value={f.shipping_address} onChange={(e) => setF((s) => ({ ...s, shipping_address: e.target.value }))} data-testid="checkout-address" />
      <button onClick={submit} disabled={busy} data-testid="checkout-submit" className="w-full h-11 rounded-lg bg-[#2F7D32] hover:bg-[#276b2a] text-white font-medium disabled:opacity-50">{busy ? "Placing…" : "Place order"}</button>
    </Modal>
  );
}

export default function ShopPage() {
  const [products, setProducts] = useState(null);
  const [cart, setCart] = useState(null);
  const [tiers, setTiers] = useState(null);
  const [ent, setEnt] = useState(null);
  const [checkout, setCheckout] = useState(false);

  const loadCart = () => TurfTalk.cartGet().then(setCart).catch(() => setCart({ items: [] }));
  useEffect(() => {
    TurfTalk.shopProducts().then((r) => setProducts(r.products || [])).catch(() => setProducts([]));
    loadCart();
    TurfTalk.plusTiers().then(setTiers).catch(() => {});
    TurfTalk.entitlement().then(setEnt).catch(() => {});
  }, []);

  const add = async (p) => { const sku = (p.variants?.[0]?.sku) || p.default_sku || p.id; try { await TurfTalk.cartAdd({ product_id: p.id, sku, qty: 1 }); toast.success("Added to cart"); loadCart(); } catch (e) { toast.error(e.message); } };
  const removeItem = async (sku) => { try { await TurfTalk.cartRemove(sku); loadCart(); } catch (e) { toast.error(e.message); } };
  const startPlus = async () => { try { await TurfTalk.membershipStart("web"); toast.success("Turf Talk+ started (sandbox)"); TurfTalk.entitlement().then(setEnt); } catch (e) { toast.error(e.message); } };

  const items = cart?.items || [];
  const subtotal = items.reduce((a, it) => a + (it.unit_price_cents || it.price_cents || 0) * (it.qty || 1), 0);

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
      <PageHeader title="Shop" subtitle="Team gear and Turf Talk+ — sandbox preview, no live charges." />
      {tiers && (
        <div className="tt-panel rounded-xl p-5 mb-6 flex items-center gap-4">
          <Crown className="text-[#F4B740]" size={22} />
          <div className="flex-1">
            <div className="text-white font-medium">Turf Talk+ <span className="text-white/40 text-xs">· {tiers.plus?.price}</span></div>
            <div className="text-white/50 text-sm">{ent?.tier === "plus" ? "Active (sandbox)" : "Upgrade for member perks. Billing deferred."}</div>
          </div>
          {ent?.tier !== "plus" && <button onClick={startPlus} data-testid="start-plus" className="px-4 h-10 rounded-lg bg-[#F4B740] text-black text-sm font-medium">Start Turf Talk+</button>}
        </div>
      )}
      <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
        <div>
          {products === null ? <div className="grid sm:grid-cols-3 gap-4">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-56" />)}</div>
            : products.length === 0 ? <Empty title="No products yet" detail="The team store is being stocked." icon={ShoppingBag} />
            : <div className="grid sm:grid-cols-3 gap-4">{products.map((p) => (
                <div key={p.id} className="tt-panel rounded-xl overflow-hidden" data-testid={`product-${p.id}`}>
                  <div className="aspect-square bg-[#0f1620]">{p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />}</div>
                  <div className="p-3"><div className="text-white text-sm truncate">{p.title}</div><div className="text-[#4ea152] font-display text-lg tabnums">{money(p.price_cents)}</div>
                    <button onClick={() => add(p)} data-testid={`add-${p.id}`} className="mt-2 w-full h-9 rounded-lg bg-[#24362A] border border-[#2F7D32] text-white text-xs">Add to cart</button></div>
                </div>
              ))}</div>}
        </div>
        <aside className="tt-panel rounded-xl p-4 lg:sticky lg:top-6" data-testid="cart">
          <div className="flex items-center gap-2 mb-3"><ShoppingCart size={16} className="text-[#4ea152]" /><span className="font-display tracking-wide text-white text-sm">CART ({items.length})</span></div>
          {items.length === 0 ? <p className="text-white/40 text-sm">Your cart is empty.</p>
            : <>
              {items.map((it) => (<div key={it.sku} className="flex items-center gap-2 py-2 border-b border-[#2A3544] last:border-0"><div className="flex-1 min-w-0"><div className="text-white text-sm truncate">{it.title}</div><div className="text-white/40 text-xs tabnums">{it.qty} × {money(it.unit_price_cents || it.price_cents || 0)}</div></div><button onClick={() => removeItem(it.sku)} data-testid={`cart-remove-${it.sku}`} className="text-white/40 hover:text-[#ff6b60]"><Trash2 size={15} /></button></div>))}
              <div className="flex justify-between mt-3 text-white"><span className="text-white/50 text-sm">Subtotal</span><span className="font-display tabnums">{money(subtotal)}</span></div>
              <button onClick={() => setCheckout(true)} data-testid="checkout-btn" className="mt-3 w-full h-10 rounded-lg bg-[#2F7D32] text-white text-sm font-medium">Checkout</button>
            </>}
        </aside>
      </div>
      <CheckoutModal open={checkout} onClose={() => setCheckout(false)} onDone={() => loadCart()} />
    </div>
  );
}
